/**
 * MAHI — stale-while-revalidate cache for the profile screens.
 *
 * The wardrobe, chat history and wardrobe count are all things a user opens,
 * backs out of, and opens again within the same few minutes. Every one of
 * those re-opens used to be a cold round trip behind a spinner, because the
 * page fetched on mount and kept nothing.
 *
 * So: paint from the last known answer immediately, fetch in the background,
 * and swap in the fresh one when it lands. The screen is never blank for a
 * returning visitor, and the network stops being on the critical path for
 * anything but the very first visit.
 *
 * Why localStorage and not HTTP caching: these responses are per-user and
 * behind a bearer token. `Cache-Control` would put them in a shared browser
 * cache keyed only by URL, which is both wrong (the token, not the URL,
 * decides what comes back) and unusable at the CDN for the same reason. An
 * explicit application cache is the honest version of the same idea.
 *
 * Why not sessionStorage: the point is the *second visit*, including the one
 * that happens tomorrow. sessionStorage dies with the tab.
 *
 * Everything here is best-effort. Private mode, a full quota, and a browser
 * with site data blocked all throw on plain reads and writes, and in every one
 * of those cases the right outcome is the behaviour we had before this
 * existed — a normal fetch — never a broken screen.
 */

const NAMESPACE = 'mahi_swr:';

// How old a cached answer may be and still be shown while we revalidate.
// Past this it is dropped unread and the screen waits for the network, which
// is the right trade for someone coming back after a week to a wardrobe they
// have since changed on another device.
const MAX_AGE_MS = 24 * 60 * 60 * 1000;

// Payloads above this are fetched but not stored. A 200-item wardrobe lands
// around 400 KB; something several times that is a sign the shape changed, and
// filling a 5 MB quota with one entry would evict everything else here.
const MAX_ENTRY_BYTES = 1_000_000;

/** The current user's id, for scoping. Null when signed out or unknown. */
function currentUserId() {
  try {
    const raw = localStorage.getItem('mahi_user_cache');
    return raw ? (JSON.parse(raw)?.user_id || null) : null;
  } catch {
    return null;
  }
}

/**
 * Full storage key for a logical name.
 *
 * Scoped by user id so that signing in as someone else on a shared device can
 * never paint the previous account's wardrobe — the keys simply don't collide.
 * Returns null when there is no user, which callers treat as "don't cache":
 * an anonymous visitor has nothing here worth keeping.
 */
function scopedKey(name) {
  const userId = currentUserId();
  return userId ? `${NAMESPACE}${userId}:${name}` : null;
}

/** Drop every entry this module owns, for every user on this device. */
export function clearCached() {
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith(NAMESPACE))
      .forEach((k) => localStorage.removeItem(k));
  } catch { /* nothing readable to clear */ }
}

/** The last stored value for `name`, or null if absent, stale or unreadable. */
export function readCached(name) {
  const key = scopedKey(name);
  if (!key) return null;
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const { at, value } = JSON.parse(raw);
    if (!at || Date.now() - at > MAX_AGE_MS) {
      localStorage.removeItem(key);
      return null;
    }
    return value;
  } catch {
    // Corrupt entry (a half-written value, or a shape from an older build) —
    // treat exactly like a miss rather than letting it throw on every mount.
    try { localStorage.removeItem(key); } catch { /* nothing to do */ }
    return null;
  }
}

/** Store `value` under `name`. Silently does nothing if it cannot. */
export function writeCached(name, value) {
  const key = scopedKey(name);
  if (!key) return;
  try {
    const payload = JSON.stringify({ at: Date.now(), value });
    if (payload.length > MAX_ENTRY_BYTES) return;
    try {
      localStorage.setItem(key, payload);
    } catch {
      // Almost always a full quota. Our own entries are the ones we're
      // entitled to evict, and the newest write is the one worth keeping.
      clearCached();
      localStorage.setItem(key, payload);
    }
  } catch { /* private mode, or still no room — the screen works without us */ }
}

/** Cache names. Here rather than inline so a rename can't silently orphan an entry. */
export const KEYS = {
  wardrobe: 'wardrobe',
  wardrobeCollections: 'wardrobe_collections',
  wardrobeCount: 'wardrobe_count',
  conversations: 'conversations',
};
